import { Suspense, lazy, useEffect } from "react";
import { ThemeProvider } from "next-themes";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import {
  createBrowserRouter,
  createRoutesFromElements,
  RouterProvider,
  Route,
  Navigate,
  useParams,
} from "react-router-dom";
import { AuthProvider } from "@/hooks/useAuth";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import { AdminLayoutRoute } from "@/components/admin/AdminLayout";
import { Loader2 } from "lucide-react";
import { getAppContext, type AppContext } from "@/lib/hostname";
import { PageLoader } from "@/components/PageLoader";
import { AppErrorFallback } from "@/components/AppErrorFallback";
import { RouteErrorFallback } from "@/components/RouteErrorFallback";
import { ErrorListener } from "@/components/feedback/ErrorListener";

// Critical path pages - loaded immediately
import Index from "./pages/Index";
import Login from "./pages/Login";
import ResetPassword from "./pages/ResetPassword";
import AuthConfirm from "./pages/AuthConfirm";
import Unauthorized from "./pages/Unauthorized";
import TagPage from "./pages/TagPage";
import NotFound from "./pages/NotFound";

// Lazy load admin pages - not needed for fan-facing experience
const InternalAdminDashboard = lazy(() => import("./pages/admin/InternalAdminDashboard"));
const ClubsPage = lazy(() => import("./pages/admin/ClubsPage"));
const StadiumsPage = lazy(() => import("./pages/admin/StadiumsPage"));
const KiosksPage = lazy(() => import("./pages/admin/KiosksPage"));
const WorkspacesPage = lazy(() => import("./pages/admin/WorkspacesPage"));
const StadiumSeatsPage = lazy(() => import("./pages/admin/StadiumSeatsPage"));
const VenueSetupPage = lazy(() => import("./pages/admin/VenueSetupPage"));
const EventsPage = lazy(() => import("./pages/admin/EventsPage"));
const LanguagesPage = lazy(() => import("./pages/admin/LanguagesPage"));
const TranslationsPage = lazy(() => import("./pages/admin/TranslationsPage"));
const LivePollsPage = lazy(() => import("./pages/admin/LivePollsPage"));

// Lazy load club admin pages
const MyPage = lazy(() => import("./pages/club/MyPage"));
const Insights = lazy(() => import("./pages/club/Insights"));
const TenantOrdersPage = lazy(() => import("./pages/club/TenantOrdersPage"));
const SettingsHub = lazy(() => import("./pages/club/SettingsHub"));
const ClubTranslationsPage = lazy(() => import("./pages/club/ClubTranslationsPage"));
const ClubEventsPage = lazy(() => import("./pages/club/ClubEventsPage"));
const MyStadiumPage = lazy(() => import("./pages/club/MyStadiumPage"));
const StadiumScreensPage = lazy(() => import("./pages/club/StadiumScreensPage"));
const SurveyEditor = lazy(() => import("./pages/club/SurveyEditor"));
const LineupEditor = lazy(() => import("./pages/club/LineupEditor"));
const ReceiptPage = lazy(() => import("./pages/receipt/ReceiptPage"));
const StadiumScreenPage = lazy(() => import("./pages/screen/StadiumScreenPage"));

// Lazy load Kiosk Portal pages
const KioskOrdersPage = lazy(() => import("./pages/kiosk/KioskOrdersPage"));
const KioskMenuPage = lazy(() => import("./pages/kiosk/KioskMenuPage"));
const KioskReportingPage = lazy(() => import("./pages/kiosk/KioskReportingPage"));
const KioskSettingsPage = lazy(() => import("./pages/kiosk/KioskSettingsPage"));

// Lazy load Stadium Portal pages
const VenueOverviewPage = lazy(() => import("./pages/venue/VenueOverviewPage"));
const VenueTenantsPage = lazy(() => import("./pages/venue/VenueTenantsPage"));
const VenueKiosksPage = lazy(() => import("./pages/venue/VenueKiosksPage"));
const VenueSeatsPage = lazy(() => import("./pages/venue/VenueSeatsPage"));
const VenueEventsPage = lazy(() => import("./pages/venue/VenueEventsPage"));
const VenueSettingsPage = lazy(() => import("./pages/venue/VenueSettingsPage"));


// Redirect fan URLs that land on admin domain to the correct go.idseat.com subdomain
const FanRedirect = () => {
  const { stadiumSlug, tagPublicId } = useParams();

  useEffect(() => {
    window.location.href = `https://go.idseat.com/${stadiumSlug}/${tagPublicId}${window.location.search}`;
  }, [stadiumSlug, tagPublicId]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
    </div>
  );
};

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 2, // 2 minutes
      gcTime: 1000 * 60 * 5, // 5 minutes
      retry: 3,
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 10000),
      refetchOnWindowFocus: false,
    },
  },
});

// Internal-admin child routes, rendered inside the persistent AdminLayoutRoute
// shell. Each carries a `handle` with the title/breadcrumbs the page used to
// pass to <AdminLayout>; the shell reads the deepest handle via useMatches().
const internalAdminRoutes = (
  <>
    <Route
      path="/admin"
      handle={{ title: 'Dashboard', breadcrumbs: [{ label: 'Internal Admin' }, { label: 'Dashboard' }] }}
      element={
        <ProtectedRoute requiredRole="idseat_admin">
          <InternalAdminDashboard />
        </ProtectedRoute>
      }
    />
    <Route
      path="/admin/clubs"
      handle={{ title: 'Clubs', breadcrumbs: [{ label: 'Internal Admin' }, { label: 'Clubs' }] }}
      element={
        <ProtectedRoute requiredRole="idseat_admin">
          <ClubsPage />
        </ProtectedRoute>
      }
    />
    <Route
      path="/admin/stadiums"
      handle={{ title: 'Stadiums', breadcrumbs: [{ label: 'Internal Admin' }, { label: 'Stadiums' }] }}
      element={
        <ProtectedRoute requiredRole="idseat_admin">
          <StadiumsPage />
        </ProtectedRoute>
      }
    />
    <Route
      path="/admin/kiosks"
      handle={{ title: 'Kiosks', breadcrumbs: [{ label: 'Internal Admin' }, { label: 'Kiosks' }] }}
      element={
        <ProtectedRoute requiredRole="idseat_admin">
          <KiosksPage />
        </ProtectedRoute>
      }
    />
    <Route
      path="/admin/workspaces"
      handle={{ title: 'Workspaces', breadcrumbs: [{ label: 'Internal Admin' }, { label: 'Workspaces' }] }}
      element={
        <ProtectedRoute requiredRole="idseat_admin">
          <WorkspacesPage />
        </ProtectedRoute>
      }
    />
    <Route
      path="/admin/venues/setup"
      handle={{
        title: 'Set up a venue',
        breadcrumbs: [
          { label: 'Internal Admin' },
          { label: 'Stadiums', href: '/admin/stadiums' },
          { label: 'Set up a venue' },
        ],
      }}
      element={
        <ProtectedRoute requiredRole="idseat_admin">
          <VenueSetupPage />
        </ProtectedRoute>
      }
    />
    <Route
      path="/admin/stadiums/:stadiumId/seats"
      handle={{
        title: 'Stadium Seats',
        breadcrumbs: [
          { label: 'Internal Admin' },
          { label: 'Stadiums', href: '/admin/stadiums' },
          { label: 'Seats' },
        ],
      }}
      element={
        <ProtectedRoute requiredRole="idseat_admin">
          <StadiumSeatsPage />
        </ProtectedRoute>
      }
    />
    <Route
      path="/admin/events"
      handle={{ title: 'Events', breadcrumbs: [{ label: 'Internal Admin' }, { label: 'Events' }] }}
      element={
        <ProtectedRoute requiredRole="idseat_admin">
          <EventsPage />
        </ProtectedRoute>
      }
    />
    <Route
      path="/admin/languages"
      handle={{ title: 'Languages', breadcrumbs: [{ label: 'Admin', href: '/admin' }, { label: 'Languages' }] }}
      element={
        <ProtectedRoute requiredRole="idseat_admin">
          <LanguagesPage />
        </ProtectedRoute>
      }
    />
    <Route
      path="/admin/translations"
      handle={{ title: 'Core Translations', breadcrumbs: [{ label: 'Admin', href: '/admin' }, { label: 'Translations' }] }}
      element={
        <ProtectedRoute requiredRole="idseat_admin">
          <TranslationsPage />
        </ProtectedRoute>
      }
    />
    <Route
      path="/admin/live-polls"
      handle={{ title: 'Live polls', breadcrumbs: [{ label: 'Admin', href: '/admin' }, { label: 'Live polls' }] }}
      element={
        <ProtectedRoute requiredRole="idseat_admin">
          <LivePollsPage />
        </ProtectedRoute>
      }
    />
  </>
);

// Club-admin child routes, rendered inside the persistent AdminLayoutRoute shell.
const clubAdminRoutes = (
  <>
    <Route
      path="/club"
      handle={{ title: 'My Page', breadcrumbs: [{ label: 'Tenant Portal' }, { label: 'My Page' }] }}
      element={
        <ProtectedRoute requiredRole="club_admin" requireClubAccess>
          <MyPage />
        </ProtectedRoute>
      }
    />
    <Route
      path="/club/events/:eventId/lineup"
      handle={{
        title: 'Lineup Editor',
        breadcrumbs: [
          { label: 'Tenant Portal', href: '/club' },
          { label: 'Events', href: '/club/events' },
          { label: 'Lineup Editor' },
        ],
      }}
      element={
        <ProtectedRoute requiredRole="club_admin" requireClubAccess>
          <LineupEditor />
        </ProtectedRoute>
      }
    />
    <Route
      path="/club/events"
      handle={{ title: 'Events', breadcrumbs: [{ label: 'Tenant Portal' }, { label: 'Events' }] }}
      element={
        <ProtectedRoute requiredRole="club_admin" requireClubAccess>
          <ClubEventsPage />
        </ProtectedRoute>
      }
    />
    <Route
      path="/club/stadium"
      handle={{ title: 'My Stadium', breadcrumbs: [{ label: 'Tenant Portal' }, { label: 'My Stadium' }] }}
      element={
        <ProtectedRoute requiredRole="club_admin" requireClubAccess>
          <MyStadiumPage />
        </ProtectedRoute>
      }
    />
    <Route
      path="/club/screens"
      handle={{ title: 'Screens', breadcrumbs: [{ label: 'Tenant Portal' }, { label: 'Screens' }] }}
      element={
        <ProtectedRoute requiredRole="club_admin" requireClubAccess>
          <StadiumScreensPage />
        </ProtectedRoute>
      }
    />
    <Route
      path="/club/survey"
      handle={{ title: 'Survey', breadcrumbs: [{ label: 'Tenant Portal' }, { label: 'Survey' }] }}
      element={
        <ProtectedRoute requiredRole="club_admin" requireClubAccess>
          <SurveyEditor />
        </ProtectedRoute>
      }
    />
    <Route
      path="/club/insights"
      handle={{ title: 'Insights', breadcrumbs: [{ label: 'Tenant Portal' }, { label: 'Insights' }] }}
      element={
        <ProtectedRoute requiredRole="club_admin" requireClubAccess>
          <Insights />
        </ProtectedRoute>
      }
    />
    <Route
      path="/club/orders"
      handle={{ title: 'Orders', breadcrumbs: [{ label: 'Tenant Portal' }, { label: 'Orders' }] }}
      element={
        <ProtectedRoute requiredRole="club_admin" requireClubAccess>
          <TenantOrdersPage />
        </ProtectedRoute>
      }
    />
    <Route
      path="/club/settings"
      handle={{ title: 'Settings', breadcrumbs: [{ label: 'Tenant Portal' }, { label: 'Settings' }] }}
      element={
        <ProtectedRoute requiredRole="club_admin" requireClubAccess>
          <SettingsHub />
        </ProtectedRoute>
      }
    />
    <Route
      path="/club/translations"
      handle={{
        title: 'Languages & translations',
        breadcrumbs: [
          { label: 'Tenant Portal' },
          { label: 'Settings', href: '/club/settings' },
          { label: 'Languages & translations' },
        ],
      }}
      element={
        <ProtectedRoute requiredRole="club_admin" requireClubAccess>
          <ClubTranslationsPage />
        </ProtectedRoute>
      }
    />
  </>
);

// Kiosk Portal child routes, rendered inside the same persistent shell. Each
// requires the active workspace to be a kiosk (operator); the shell's
// switcher moves between portals.
const kioskRoutes = (
  <>
    <Route
      path="/kiosk"
      handle={{ title: 'Orders', breadcrumbs: [{ label: 'Kiosk Portal' }, { label: 'Orders' }] }}
      element={
        <ProtectedRoute requiredRole="club_admin" workspace="operator">
          <KioskOrdersPage />
        </ProtectedRoute>
      }
    />
    <Route
      path="/kiosk/menu"
      handle={{ title: 'Menu', breadcrumbs: [{ label: 'Kiosk Portal' }, { label: 'Menu' }] }}
      element={
        <ProtectedRoute requiredRole="club_admin" workspace="operator" workspaceRole="admin">
          <KioskMenuPage />
        </ProtectedRoute>
      }
    />
    <Route
      path="/kiosk/reporting"
      handle={{ title: 'Reporting', breadcrumbs: [{ label: 'Kiosk Portal' }, { label: 'Reporting' }] }}
      element={
        <ProtectedRoute requiredRole="club_admin" workspace="operator" workspaceRole="admin">
          <KioskReportingPage />
        </ProtectedRoute>
      }
    />
    <Route
      path="/kiosk/settings"
      handle={{ title: 'Settings', breadcrumbs: [{ label: 'Kiosk Portal' }, { label: 'Settings' }] }}
      element={
        <ProtectedRoute requiredRole="club_admin" workspace="operator" workspaceRole="admin">
          <KioskSettingsPage />
        </ProtectedRoute>
      }
    />
  </>
);

// Stadium Portal child routes, rendered inside the same persistent shell. Each
// requires the active workspace to be a venue the person owns.
const venueRoute = (path: string, title: string, page: React.ReactNode) => (
  <Route
    key={path}
    path={path}
    handle={{ title, breadcrumbs: [{ label: 'Stadium Portal' }, { label: title }] }}
    element={
      <ProtectedRoute requiredRole="club_admin" workspace="venue">
        {page}
      </ProtectedRoute>
    }
  />
);
const venueRoutes = (
  <>
    {venueRoute('/venue', 'Overview', <VenueOverviewPage />)}
    {venueRoute('/venue/tenants', 'Tenants', <VenueTenantsPage />)}
    {venueRoute('/venue/kiosks', 'Kiosks', <VenueKiosksPage />)}
    {venueRoute('/venue/seats', 'Seats & Tags', <VenueSeatsPage />)}
    {venueRoute('/venue/events', 'Events', <VenueEventsPage />)}
    {venueRoute('/venue/settings', 'Settings', <VenueSettingsPage />)}
  </>
);

// Old paths → new consolidated pages. Kept outside the shell so they redirect
// instantly without briefly mounting the chrome.
const clubRedirects = (
  <>
    <Route path="/club/reporting" element={<Navigate to="/club/insights" replace />} />
    <Route path="/club/design" element={<Navigate to="/club" replace />} />
    <Route path="/club/features" element={<Navigate to="/club" replace />} />
    <Route path="/club/live" element={<Navigate to="/club/orders" replace />} />
    <Route path="/club/billing" element={<Navigate to="/club/settings" replace />} />
    <Route path="/club/language" element={<Navigate to="/club/settings" replace />} />
    <Route path="/club/profile" element={<Navigate to="/club/settings" replace />} />
  </>
);

// Admin Routes Component (handles both IDSeat admin and Club admin)
const AdminRoutes = () => (
  <>
    <Route path="/" element={<Index />} />
    <Route path="/login" element={<Login />} />
    <Route path="/reset-password" element={<ResetPassword />} />
    <Route path="/auth/confirm" element={<AuthConfirm />} />
    <Route path="/unauthorized" element={<Unauthorized />} />

    {/* Persistent admin shell — sidebar/topbar and global effects survive
        navigation; only the Outlet content swaps. Per-child ProtectedRoute
        guards are unchanged; Suspense now lives in AdminLayoutRoute. */}
    <Route element={<AdminLayoutRoute />}>
      {internalAdminRoutes}
      {clubAdminRoutes}
      {kioskRoutes}
      {venueRoutes}
    </Route>

    {clubRedirects}

    {/* Public receipt page — security via UUID unguessability */}
    <Route path="/receipt/:orderId" element={
      <Suspense fallback={<PageLoader />}>
        <ReceiptPage />
      </Suspense>
    } />

    {/* Public stadium screens — security via the club's secret token in the URL */}
    <Route path="/screen/:token/:screen/:tagPublicId?" element={
      <Suspense fallback={<PageLoader />}>
        <StadiumScreenPage />
      </Suspense>
    } />

    {/* Enable internal fan page preview even in admin context (to avoid redirect loop) */}
    <Route path="/t/PREVIEW" element={<TagPage />} />

    {/* Catch fan URLs that land on admin domain and redirect to go.idseat.com */}
    <Route path="/:stadiumSlug/:tagPublicId" element={<FanRedirect />} />

    <Route path="*" element={<NotFound />} />
  </>
);

// Development Routes - shows all routes for testing on localhost
const DevelopmentRoutes = () => (
  <>
    <Route path="/" element={<Index />} />
    <Route path="/login" element={<Login />} />
    <Route path="/reset-password" element={<ResetPassword />} />
    <Route path="/auth/confirm" element={<AuthConfirm />} />
    <Route path="/unauthorized" element={<Unauthorized />} />

    {/* Persistent admin shell (see AdminRoutes). */}
    <Route element={<AdminLayoutRoute />}>
      {internalAdminRoutes}
      {clubAdminRoutes}
      {kioskRoutes}
      {venueRoutes}
    </Route>

    {clubRedirects}

    {/* Public receipt page — security via UUID unguessability */}
    <Route path="/receipt/:orderId" element={
      <Suspense fallback={<PageLoader />}>
        <ReceiptPage />
      </Suspense>
    } />

    {/* Public stadium screens — security via the club's secret token in the URL */}
    <Route path="/screen/:token/:screen/:tagPublicId?" element={
      <Suspense fallback={<PageLoader />}>
        <StadiumScreenPage />
      </Suspense>
    } />

    {/* Fan Page Routes in Dev Context */}
    <Route path="/:stadiumSlug/:tagPublicId" element={<TagPage />} />
    <Route path="/t/:tagPublicId" element={<TagPage />} />

    <Route path="*" element={<NotFound />} />
  </>
);

// Fan-facing Routes Component (go.idseat.com)
const FanRoutes = () => (
  <>
    <Route path="/" element={<Index />} />
    <Route path="/login" element={<Login />} />
    <Route path="/reset-password" element={<ResetPassword />} />
    <Route path="/auth/confirm" element={<AuthConfirm />} />
    <Route path="/receipt/:orderId" element={
      <Suspense fallback={<PageLoader />}>
        <ReceiptPage />
      </Suspense>
    } />

    {/* Public stadium screens — security via the club's secret token in the URL */}
    <Route path="/screen/:token/:screen/:tagPublicId?" element={
      <Suspense fallback={<PageLoader />}>
        <StadiumScreenPage />
      </Suspense>
    } />
    <Route path="/:stadiumSlug/:tagPublicId" element={<TagPage />} />
    <Route path="*" element={<NotFound />} />
  </>
);

// Route selector based on hostname
const routesForContext = (context: AppContext) => {
  switch (context) {
    case 'admin':
      return AdminRoutes();
    case 'fan':
      return FanRoutes();
    case 'development':
      return DevelopmentRoutes();
    default:
      return FanRoutes();
  }
};

// Build the data router once for the current hostname context. A data router
// (createBrowserRouter) is required so route `handle`s and useMatches() work.
const router = createBrowserRouter(
  createRoutesFromElements(
    // Pathless parent so one error element covers every route. Without it a
    // render error is caught by react-router's default boundary and never
    // reaches Sentry (the Sentry.ErrorBoundary below sits outside the router).
    <Route errorElement={<RouteErrorFallback />}>
      {routesForContext(getAppContext())}
    </Route>,
  ),
);

import { createAsyncStoragePersister } from "@tanstack/query-async-storage-persister";
import { PersistQueryClientProvider } from "@tanstack/react-query-persist-client";

const persister = createAsyncStoragePersister({
  storage: window.localStorage,
  key: "IDSEAT_QUERY_CACHE",
});

// Bust the persisted cache whenever the build changes so a fresh deploy never
// rehydrates data shaped by old code (this was a recurring source of
// stale-looking screens after releases). __BUILD_ID__ is injected by Vite.
declare const __BUILD_ID__: string;
const persistBuster = typeof __BUILD_ID__ !== "undefined" ? __BUILD_ID__ : "dev";

import * as Sentry from "@sentry/react";

const App = () => (
  <ThemeProvider attribute="class" defaultTheme="light">
  {/* A crash must look like a crash. This used to fall back to <PageLoader />,
      so every error rendered as an endless spinner that nobody — user or
      support — could distinguish from a slow network. */}
  <Sentry.ErrorBoundary
    fallback={({ error, eventId, resetError }) => (
      <AppErrorFallback error={error} eventId={eventId} resetError={resetError} />
    )}
  >
    <PersistQueryClientProvider
      client={queryClient}
      persistOptions={{ persister, buster: persistBuster, maxAge: 1000 * 60 * 60 * 24 }}
    >
      <AuthProvider>
        <TooltipProvider>
          <Toaster />
          <Sonner />
          <ErrorListener />
          <RouterProvider router={router} />
        </TooltipProvider>
      </AuthProvider>
    </PersistQueryClientProvider>
  </Sentry.ErrorBoundary>
  </ThemeProvider>
);

export default App;
